import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authService } from "@/features/auth/services/auth.service";
import { useAuthStore } from "@/stores/authStore";
import { getFirebaseAuth, resetFirebaseClient } from "@/lib/firebase";
import { ROUTES } from "@/app/constants";

/**
 * Logs out end-to-end:
 *   1. POST /auth/logout   → server revokes refresh + access JTI, clears cookies
 *   2. Firebase signOut    → clears the phone-auth IndexedDB session so a
 *                            re-login on the same browser starts clean
 *   3. resetFirebaseClient → drops the memoized reCAPTCHA verifier so the
 *                            next `signInWithPhoneNumber` mounts a fresh one
 *   4. Local state         → auth store cleared, React Query cache cleared,
 *                            redirect to /login
 *
 * All four steps run in `onSettled` so the client is signed out even if
 * the server call fails (network dropped, cookie already expired, etc.).
 */
export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSettled: async () => {
      try {
        const { signOut } = await import("firebase/auth");
        const auth = await getFirebaseAuth();
        await signOut(auth);
      } catch {
        // Firebase may never have initialized this session (email+password
        // login path). That's fine — nothing to sign out of.
      }
      resetFirebaseClient();
      logout();
      queryClient.clear();
      navigate(ROUTES.login, { replace: true });
    },
  });
}
